import React, { useState } from 'react';
import { ExternalLink, Heart, MapPin, MessageCircle, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { safeWorkUrl } from '../artistProfile';
import { youtubeVideoId } from '../youtube';
import { postLocation, setPostLiked, usePostStats } from '../services/postInteractions';
import { displayName, formatTime } from '../utils';
import { Avatar } from './UI';
import ArtistProfileSummary from './ArtistProfileSummary';
import { distanceKm } from '../locationFeed';

export default function PostCard({ post, author, onDelete, deleting = false, detail = false, center }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { likeCount, liked, commentCount } = usePostStats(post.id, user?.uid);
  const [pendingLike, setPendingLike] = useState(null);
  const [error, setError] = useState('');
  const shownLiked = pendingLike ?? liked;
  const shownLikeCount = likeCount + (pendingLike === null || pendingLike === liked ? 0 : pendingLike ? 1 : -1);
  const authorProfile = { username: author?.username || post.authorUsername, photoURL: author?.photoURL || post.authorPhotoURL };
  const location = postLocation(post, author);
  const distance = distanceKm(center, post);
  const url = safeWorkUrl(post.workUrl);
  const videoId = youtubeVideoId(post.workUrl);
  const hasVideoThumbnail = Boolean(videoId && post.imageUrl);
  const postPath = `/posts/${post.id}`;

  const toggleLike = async () => {
    if (!user) { navigate('/login'); return; }
    if (pendingLike !== null) return;
    const next = !liked;
    setPendingLike(next); setError('');
    try { await setPostLiked(post.id, user.uid, next); }
    catch (err) { setError(err.message || 'Unable to update your like.'); }
    finally { setPendingLike(null); }
  };

  const body = <div className={hasVideoThumbnail ? 'post-description post-description--thumbnail' : 'post-description'}>
    {post.imageUrl && <img className={hasVideoThumbnail ? 'post-thumbnail' : 'shared-work-image'} src={post.imageUrl} alt={`Work shared by ${displayName(authorProfile)}`} loading="lazy" />}
    <p className="work-text">{post.text}</p>
  </div>;

  return <article className={`work-card feed-card ${detail ? 'feed-card--detail' : ''}`}>
    <header>
      <Link className="work-author" to={`/profile/${post.userId}`}>
        <Avatar profile={authorProfile} size={42} />
        <div>
          <strong>@{displayName(authorProfile)}</strong>
          {location && <span className="post-location"><MapPin size={12} aria-hidden="true" />{location}{distance !== null && ` · ${distance < 1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(1)} km`} away`}</span>}
          <time>{formatTime(post.createdAt)}</time>
        </div>
      </Link>
      {onDelete && post.userId === user?.uid && <button className="icon-button danger" onClick={() => onDelete(post)} disabled={deleting} aria-label="Delete post"><Trash2 size={16} /></button>}
    </header>
    {detail ? body : <Link className="feed-card-body" to={postPath} aria-label={`Open post by ${displayName(authorProfile)}`}>{body}</Link>}
    {videoId && <div className="post-video">
      <iframe src={`https://www.youtube-nocookie.com/embed/${videoId}`} title={`YouTube video shared by ${displayName(authorProfile)}`}
        loading="lazy" referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen />
    </div>}
    <ArtistProfileSummary profile={{ professions: post.professions, artCategories: post.artCategories, musicianSubcategories: post.musicianSubcategories }} />
    {url && <a className="text-link" href={url} target="_blank" rel="noopener noreferrer">{videoId ? 'Watch on YouTube' : 'Explore this work'} <ExternalLink size={15} /></a>}
    <footer className="post-actions">
      <button type="button" className={`post-action ${shownLiked ? 'liked' : ''}`} onClick={toggleLike} aria-pressed={shownLiked} aria-label={shownLiked ? 'Unlike' : 'Like'} disabled={pendingLike !== null}>
        <Heart size={18} fill={shownLiked ? 'currentColor' : 'none'} /><span>{shownLikeCount} {shownLikeCount === 1 ? 'like' : 'likes'}</span>
      </button>
      {detail
        ? <span className="post-action post-action--static"><MessageCircle size={18} /><span>{commentCount} {commentCount === 1 ? 'comment' : 'comments'}</span></span>
        : <Link className="post-action" to={postPath} aria-label={`${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}, open discussion`}><MessageCircle size={18} /><span>{commentCount} {commentCount === 1 ? 'comment' : 'comments'}</span></Link>}
      {user?.uid && post.userId !== user.uid && <Link className="text-link post-connect" to={`/message/user/${post.userId}`}>Message</Link>}
    </footer>
    {error && <p className="form-error" role="alert">{error}</p>}
  </article>;
}
